import classnames from "classnames";
import { Subtitle, useSubtitle } from "funkis-template/hooks/subtitles";
import { PageItemMedia } from "funkis-template/models/player";
import gsap from "gsap";
import React, { useEffect, useRef, useState } from "react";
import { VideoState } from "./VideoPlayer";
import Style from "./VideoPlayerSubtitles.module.css";

type VideoPlayerSubtitlesProps = {
	src: PageItemMedia;
	currentTime: number;
	variant: "overlay" | "block";
	videoState: VideoState;
};

const VideoPlayerSubtitles: React.FC<VideoPlayerSubtitlesProps> = ({
	currentTime,
	src,
	variant,
	videoState,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const subitleContaninerRef = useRef<HTMLDivElement>(null);
	const fadeInOutTimeline = useRef<gsap.core.Timeline>();
	const [isAnimating, setIsAnimating] = useState(false);

	const subtitle = useSubtitle(src, currentTime);
	const [currentlyShowingSubtitle, setCurrentlyShowingSubtitle] =
		useState<Subtitle | undefined>();

	useEffect(() => {
		let ctx = gsap.context(() => {
			fadeInOutTimeline.current = gsap
				.timeline({
					paused: true,
					onStart: () => {
						setIsAnimating(true);
					},
					onReverseComplete: () => {
						setIsAnimating(false);
						setCurrentlyShowingSubtitle(undefined);
					},
					onComplete: () => {
						setIsAnimating(false);
					},
				})
				.fromTo(
					subitleContaninerRef.current,
					{ alpha: 0 },
					{ alpha: 1, duration: 0.2 }
				);
		}, subitleContaninerRef);

		return () => ctx.revert();
	}, []);

	useEffect(() => {
		/**
		If we're already animating: Don't do anything.
		isAnimating is set to false once the animation is 
		completed and the new animation will be added.0s
		**/
		if (isAnimating) return;

		if (subtitle && !currentlyShowingSubtitle) {
			/**
			When we *should* display a subtitle but isn't:
			we play the animation forwards to fade in.
			**/
			setCurrentlyShowingSubtitle(subtitle);
			fadeInOutTimeline.current?.play();
		} else if (!subtitle && currentlyShowingSubtitle) {
			/**
			When we *shouldn't* display a subtitle but we are:
			we play the animation in reverse to fade out.
			**/
			fadeInOutTimeline.current?.reverse();
		} else if (
			subtitle &&
			currentlyShowingSubtitle &&
			subtitle.id !== currentlyShowingSubtitle.id
		) {
			/**
			We also need to check the subtitle ids in case
			the user has seeked in the video.
			We fade out the subtitle if we're currently not showing
			the correct one.
			**/
			fadeInOutTimeline.current?.reverse();
		}
	}, [isAnimating, currentlyShowingSubtitle, subtitle]);

	useEffect(() => {
		if (variant !== "block") return;
		if (!containerRef.current) return;

		if (videoState === "ended") {
			gsap.to(containerRef.current, {
				height: 0,
				minHeight: 0,
				padding: 0,
				duration: 0.3,
				delay: 0.5,
			});
		} else if (videoState === "idle") {
			gsap.set(containerRef.current, {
				minHeight: 0,
				height: 0,
				padding: 0,
			});
		} else {
			gsap.to(containerRef.current, {
				duration: 0.5,
				height: "auto",
				minHeight: "var(--video-player-subtitles-min-height)",
				clearProps: "padding",
			});
		}
	}, [videoState, variant]);

	return (
		<div
			ref={containerRef}
			className={classnames(Style.subtitles, Style[variant], {
				[Style.hidden]: subtitle === undefined,
				[Style.collapsed]: videoState === "ended" || videoState === "idle",
			})}
		>
			<div ref={subitleContaninerRef}>
				{currentlyShowingSubtitle && (
					<p
						className={classnames(Style.subtitle)}
						dangerouslySetInnerHTML={{ __html: currentlyShowingSubtitle.text }}
					/>
				)}
			</div>
		</div>
	);
};

export default VideoPlayerSubtitles;
